import React, { useState, useEffect } from 'react';
import PublicHeader from '../../components/PublicHeader';
import PublicFooter from '../../components/PublicFooter';
import '../../privacy-policy.css';

const TermsAndConditions = () => {
  const [activeSection, setActiveSection] = useState('enrollment');

  useEffect(() => {
    document.title = "Terms & Conditions | PranaFlow Academy";
    const metaDesc = document.querySelector('meta[name="description"]');
    if (metaDesc) {
      metaDesc.setAttribute('content', "Read the official Terms & Conditions of PranaFlow Academy, covering enrollments, fees, classroom guidelines, certificate terms, and account rules.");
    }

    let canonical = document.querySelector('link[rel="canonical"]');
    if (!canonical) {
      canonical = document.createElement('link');
      canonical.setAttribute('rel', 'canonical');
      document.head.appendChild(canonical);
    }
    canonical.setAttribute('href', 'https://pranaflowacademy.com/terms-and-conditions');

    window.scrollTo(0, 0);
  }, []);

  // Track active section during scroll
  useEffect(() => {
    const sections = document.querySelectorAll('.privacy-doc-section');
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setActiveSection(entry.target.id);
          }
        });
      },
      { rootMargin: '-10% 0px -70% 0px' }
    );

    sections.forEach((sec) => observer.observe(sec));
    return () => sections.forEach((sec) => observer.unobserve(sec));
  }, []);

  const scrollToSection = (id) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'start' });
      setActiveSection(id);
    }
  };

  const tocItems = [
    { id: 'enrollment', label: '1. Course Enrollment' },
    { id: 'payments', label: '2. Payment & Fees' },
    { id: 'classroom', label: '3. Class & Recording Access' },
    { id: 'accounts', label: '4. Student Account Rules' },
    { id: 'teachers', label: '5. Teacher & Partner Terms' },
    { id: 'certificates', label: '6. Evaluation & Certificates' },
    { id: 'communications', label: '7. WhatsApp & Email' },
    { id: 'liability', label: '8. Limitation of Liability' },
    { id: 'contact', label: '9. Contact Information' }
  ];

  return (
    <div className="privacy-page-container">
      <PublicHeader />

      {/* Hero Header */}
      <section className="privacy-hero">
        <div className="container">
          <span className="privacy-last-updated-badge">Last Updated: 10 July 2026</span>
          <h1 className="privacy-hero-title">Terms & Conditions</h1>
          <p className="privacy-hero-subtitle">
            Please read these terms carefully before enrolling in our courses or using the PranaFlow Academy student portal.
          </p>
        </div>
      </section>

      {/* Micro Trust Cards */}
      <section className="privacy-trust-grid">
        <div className="privacy-trust-card">
          <div className="privacy-trust-icon-wrapper">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor">
              <path d="M12 2L2 7l10 5 10-5-10-5zM2 17l10 5 10-5M2 12l10 5 10-5"></path>
            </svg>
          </div>
          <h4 className="privacy-trust-title">Enrollment Terms</h4>
          <p className="privacy-trust-desc">Guidelines and prerequisites for joining our certified training cohorts.</p>
        </div>
        <div className="privacy-trust-card">
          <div className="privacy-trust-icon-wrapper">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor">
              <rect x="1" y="4" width="22" height="16" rx="2" ry="2"></rect>
              <line x1="1" y1="10" x2="23" y2="10"></line>
            </svg>
          </div>
          <h4 className="privacy-trust-title">Payment Plans</h4>
          <p className="privacy-trust-desc">Installment timelines and security policies for all manual and online payments.</p>
        </div>
        <div className="privacy-trust-card">
          <div className="privacy-trust-icon-wrapper">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor">
              <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"></path>
            </svg>
          </div>
          <h4 className="privacy-trust-title">Account Rules</h4>
          <p className="privacy-trust-desc">Safety guidelines, password reset guards, and user access policies.</p>
        </div>
        <div className="privacy-trust-card">
          <div className="privacy-trust-icon-wrapper">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor">
              <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"></path>
              <polyline points="22 4 12 14.01 9 11.01"></polyline>
            </svg>
          </div>
          <h4 className="privacy-trust-title">Valid Certifications</h4>
          <p className="privacy-trust-desc">Attendance and grading thresholds required to verify certificates.</p>
        </div>
      </section>

      {/* Split Layout */}
      <main className="privacy-split-wrapper">
        {/* Table of Contents sidebar */}
        <aside className="privacy-sidebar-nav">
          <h4 className="privacy-sidebar-title">Terms Navigation</h4>
          <ul className="privacy-toc-list">
            {tocItems.map((item) => (
              <li
                key={item.id}
                className={`privacy-toc-item ${activeSection === item.id ? 'active' : ''}`}
              >
                <button type="button" onClick={() => scrollToSection(item.id)}>
                  {item.label}
                </button>
              </li>
            ))}
          </ul>
        </aside>

        {/* Right side content */}
        <article className="privacy-content-card">
          <div className="privacy-intro-section">
            <p>Welcome to PranaFlow Academy.</p>
            <p>
              These Terms and Conditions govern your enrollment, payments, classroom codes of conduct, digital services, and portal access at PranaFlow Academy. By enrolling in a course, paying a fee installment, or registering a student/teacher account, you agree to comply with and be bound by these terms.
            </p>
          </div>

          {/* Section 1 */}
          <section id="enrollment" className="privacy-doc-section">
            <h2>1. Course Enrollment Terms</h2>
            <p>By enrolling in any online or offline training program at PranaFlow Academy, you agree to comply with our academic standards, curriculum schedules, and safety guidelines.</p>
            <p>Key terms include:</p>
            <ul>
              <li>Students must provide accurate personal and contact details during enrollment.</li>
              <li>PranaFlow Academy reserves the right to deny admission or cancel enrollment of any student who provides false information or violates classroom decorum.</li>
              <li>Eligibility for specific advanced programs (e.g., 300-Hour or 500-Hour TTC) requires submission of prerequisite certificates (e.g., 200-Hour TTC) prior to program commencement.</li>
            </ul>
          </section>

          <hr className="privacy-divider" />

          {/* Section 2 */}
          <section id="payments" className="privacy-doc-section">
            <h2>2. Payment and Fee Terms</h2>
            <p>All fees are payable in full or in agreed installments via our secure SSL-encrypted payment gateways (Razorpay) or official manual billing receipts.</p>
            <p>Important details:</p>
            <ul>
              <li><strong>Installment Plans:</strong> If you select an installment payment option, payments must be settled on or before their respective due dates.</li>
              <li><strong>Suspension:</strong> Failure to pay installments on time may result in temporary suspension of course dashboard access, live classroom sessions, and certificate eligibility.</li>
              <li><strong>Refund Policy:</strong> All program deposits and fees paid are non-refundable and non-transferable, except under explicit, documented written consent from the Academy administration.</li>
            </ul>
          </section>

          <hr className="privacy-divider" />

          {/* Section 3 */}
          <section id="classroom" className="privacy-doc-section">
            <h2>3. Class & Recording Access</h2>
            <p>Enrolled students gain access to live classes and/or recorded sessions according to their cohort settings.</p>
            <ul>
              <li><strong>Access License:</strong> Recording links, lecture portals, and course documentation are licensed for your personal educational use only. Sharing account logins, course materials, or video links with non-enrolled individuals is strictly prohibited.</li>
              <li><strong>Attendance:</strong> Students enrolled in certification programs must meet minimum attendance requirements (typically 80% or more of live sessions) to qualify for final evaluations.</li>
            </ul>
          </section>

          <hr className="privacy-divider" />

          {/* Section 4 */}
          <section id="accounts" className="privacy-doc-section">
            <h2>4. Student Account Rules</h2>
            <p>Every student registered at PranaFlow Academy is assigned a unique account in our system. You must adhere to the following security protocols:</p>
            <ul>
              <li><strong>Password Lock:</strong> Upon first login (using temporary credentials), students must immediately update their password. Dashboard and course access will remain locked until a new secure password is configured.</li>
              <li><strong>Credentials Confidentiality:</strong> You are solely responsible for maintaining the confidentiality of your credentials. Sharing login access is a violation of our terms and will lead to automatic account deactivation.</li>
            </ul>
          </section>

          <hr className="privacy-divider" />

          {/* Section 5 */}
          <section id="teachers" className="privacy-doc-section">
            <h2>5. Teacher & Partner Terms</h2>
            <p>Instructors applying to join our faculty partner system must comply with professional, legal, and academic standards:</p>
            <ul>
              <li>All credentials, profiles, bios, and certificates submitted for admin review must be genuine.</li>
              <li>Teachers are assigned batches at the discretion of the admin and must conduct classes as per the cohort schedule.</li>
              <li>Instructors must respect student confidentiality and refrain from sharing dashboard student information externally.</li>
            </ul>
          </section>

          <hr className="privacy-divider" />

          {/* Section 6 */}
          <section id="certificates" className="privacy-doc-section">
            <h2>6. Evaluation & Certificate Terms</h2>
            <p>PranaFlow Academy certificates are awarded only upon successful completion of program requirements:</p>
            <ul>
              <li>Students must attempt and pass all theoretical and practical exams with passing grades.</li>
              <li>Certificates will be issued digitally by instructors or admin panel once grading is finalized.</li>
              <li>Certificates are issued with a unique verification code and Drive download link.</li>
              <li>Any form of academic dishonesty (cheating, plagiarizing, or sharing exam papers) results in immediate disqualification and deactivation of the student portal.</li>
            </ul>
          </section>

          <hr className="privacy-divider" />

          {/* Section 7 */}
          <section id="communications" className="privacy-doc-section">
            <h2>7. WhatsApp & Email Communications</h2>
            <p>By registering, you consent to receive alerts from PranaFlow Academy via WhatsApp and Email:</p>
            <ul>
              <li>Critical updates like payment receipts, installment overdue notices, exam-ready alerts, and certificate issuances will be sent via Meta Cloud API or SMTP.</li>
              <li>Promotional or campaign alerts for new batches are sent via marketing channels. You can opt-out of promotional alerts anytime; however, critical transactional alerts will remain active as long as you are enrolled.</li>
            </ul>
          </section>

          <hr className="privacy-divider" />

          {/* Section 8 */}
          <section id="liability" className="privacy-doc-section">
            <h2>8. Limitation of Liability</h2>
            <p>Yoga involves physical movement and exercise. By participating in online or offline training, you represent that you are in good physical health and fit to practice yoga safely.</p>
            <p>PranaFlow Academy, its admin, teachers, and partners assume no liability for personal injuries, health problems, or damages suffered during online or offline classes, exercises, or programs.</p>
          </section>

          <hr className="privacy-divider" />

          {/* Section 9 / Contact */}
          <section id="contact" className="privacy-doc-section">
            <h2>9. Contact Information</h2>
            <p>If you have questions regarding these Terms and Conditions or wish to seek clarifications on academy guidelines, feel free to contact us:</p>
            
            <div className="privacy-cta-section">
              <h4 className="privacy-cta-title">Have questions about our Terms?</h4>
              <p className="privacy-cta-desc">Feel free to connect with our support team via email or WhatsApp anytime.</p>
              
              <div className="privacy-cta-buttons">
                <a href="mailto:pranaflowacademy@gmail.com" className="privacy-cta-btn email">
                  <svg viewBox="0 0 24 24">
                    <path d="M20 4H4c-1.1 0-1.99.9-1.99 2L2 18c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2zm0 4l-8 5-8-5V6l8 5 8-5v2z"/>
                  </svg>
                  <span>pranaflowacademy@gmail.com</span>
                </a>
                <a href="https://wa.me/919548354084" target="_blank" rel="noopener noreferrer" className="privacy-cta-btn whatsapp">
                  <svg viewBox="0 0 24 24">
                    <path d="M.057 24l1.687-6.163c-1.041-1.804-1.588-3.849-1.587-5.946C.06 5.348 5.397.01 12.008.01c3.202.001 6.212 1.246 8.477 3.514 2.266 2.268 3.507 5.28 3.505 8.484-.004 6.657-5.34 11.997-11.953 11.997-2.005-.001-3.973-.502-5.731-1.456L0 24zm6.59-4.846c1.6.95 3.188 1.449 4.825 1.451 5.436 0 9.859-4.407 9.862-9.823.002-2.623-1.017-5.09-2.871-6.944-1.853-1.854-4.321-2.87-6.944-2.872-5.442 0-9.863 4.41-9.865 9.827-.001 1.771.468 3.498 1.36 5.03l-.993 3.624 3.731-.977c-.105-.045-.2-.09-.285-.118zm10.741-7.234c-.29-.145-1.716-.848-1.982-.944-.266-.096-.46-.145-.653.145-.194.291-.75.944-.919 1.138-.169.194-.339.218-.629.073-.29-.145-1.226-.452-2.335-1.441-.864-.771-1.447-1.724-1.616-2.015-.17-.29-.018-.447.127-.591.131-.13.29-.339.436-.509.145-.17.194-.291.29-.485.097-.194.049-.364-.025-.509-.073-.145-.653-1.575-.895-2.156-.236-.569-.475-.491-.653-.5H6.99c-.194 0-.509.073-.775.364-.266.291-1.017.994-1.017 2.424s1.041 2.812 1.186 3.006c.145.194 2.049 3.129 4.965 4.385.694.299 1.236.478 1.658.613.697.221 1.332.19 1.833.115.559-.084 1.716-.703 1.958-1.382.242-.679.242-1.261.169-1.382-.072-.12-.266-.193-.556-.339z"/>
                  </svg>
                  <span>WhatsApp Chat</span>
                </a>
              </div>
            </div>
          </section>
        </article>
      </main>

      <PublicFooter />
    </div>
  );
};

export default TermsAndConditions;
